// Global dropdown registry
const DropdownRegistry = {
  instances: new Set(),

  register(dropdown) {
    this.instances.add(dropdown);
  },

  unregister(dropdown) {
    this.instances.delete(dropdown);
  },

  closeAll(except = null) {
    this.instances.forEach(dropdown => {
      if (dropdown !== except && dropdown.isOpen) {
        dropdown.close();
      }
    });
  },

  closeAllOnOutsideClick(event) {
    // Check if click is inside any dropdown
    const clickedDropdown = Array.from(this.instances).find(dropdown =>
      dropdown.contains(event.target)
    );

    if (!clickedDropdown) {
      this.closeAll();
    }
  }
};

class Dropdown extends HTMLElement {
  constructor() {
    super();

    // Get dropdown elements
    this.trigger = this.querySelector('[js-dropdown="trigger"]');
    this.optionsContainer = this.querySelector('[js-dropdown="options"]');
    this.selectedIcon = this.querySelector('[js-dropdown="selected-icon"]');
    this.selectedLabel = this.querySelector('[js-dropdown="selected-label"]');
    this.hiddenInput = this.querySelector('[js-dropdown="hidden-input"]');

    // Get dropdown configuration
    this.name = this.dataset.name || '';
    this.isRequired = this.dataset.required === 'true';

    // State
    this.isOpen = false;
    this.selectedValue = this.hiddenInput?.value || '';
  }

  connectedCallback() {
    // Register this dropdown instance
    DropdownRegistry.register(this);

    // Set up ARIA attributes
    this.setupAriaAttributes();

    // Make options non-focusable initially (closed by default)
    this.updateOptionsTabindex(false);

    this.setupEventListeners();
    this.setupKeyboardNavigation();
    this.setupGlobalClickOutside();
    this.setupResizeHandler();
  }

  disconnectedCallback() {
    // Unregister this dropdown instance
    DropdownRegistry.unregister(this);

    // Clean up event listeners
    document.removeEventListener('keydown', this.handleKeyDown);

    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
  }

  setupEventListeners() {
    // Trigger click
    this.trigger?.addEventListener('click', this.handleTriggerClick.bind(this));

    // Option clicks
    const options = this.querySelectorAll('[js-dropdown="option"]');

    options.forEach(option => {
      option.addEventListener('click', this.handleOptionClick.bind(this));
    });

    // Handle focusout to close dropdown when focus leaves
    this.addEventListener('focusout', this.handleFocusOut.bind(this));
  }

  handleFocusOut(event) {
    // Check if focus is moving to an element outside the dropdown
    // Use setTimeout to check after focus has moved
    setTimeout(() => {
      const activeElement = document.activeElement;
      // If focus is not within this dropdown (and not moving to another option via Tab),
      // close the dropdown
      if (!this.contains(activeElement) && this.isOpen) {
        this.close();
      }
    }, 0);
  }

  setupKeyboardNavigation() {
    this.addEventListener('keydown', this.handleKeyDown.bind(this));
  }

  setupGlobalClickOutside() {
    // Set up global click outside handler only once
    if (!DropdownRegistry.globalClickHandlerSetup) {
      document.addEventListener('click', DropdownRegistry.closeAllOnOutsideClick.bind(DropdownRegistry));

      // Also handle global Escape key to close all dropdowns
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          DropdownRegistry.closeAll();
        }
      });

      DropdownRegistry.globalClickHandlerSetup = true;
    }
  }

  setupResizeHandler() {
    // Debounce resize handler for performance
    let resizeTimeout;
    this.resizeHandler = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (this.isOpen) {
          this.adjustDropdownPosition();
        }
      }, 150);
    };

    window.addEventListener('resize', this.resizeHandler);
  }

  setupAriaAttributes() {
    // Generate unique ID for the trigger if it doesn't have one
    if (this.trigger && !this.trigger.id) {
      this.trigger.id = `dropdown-trigger-${Math.random().toString(36).substr(2, 9)}`;
    }

    // Set aria-labelledby on listbox to point to trigger
    // This ensures the listbox is properly labeled even if ID is set in template
    if (this.optionsContainer && this.trigger?.id) {
      this.optionsContainer.setAttribute('aria-labelledby', this.trigger.id);
    }
  }

  updateOptionsTabindex(isOpen) {
    const options = this.querySelectorAll('[js-dropdown="option"]');

    options.forEach(option => {
      if (isOpen) {
        // Make options focusable via Tab when open
        option.tabIndex = 0;
      } else {
        // Ensure options are not focusable when closed
        option.tabIndex = -1;
      }
    });
  }

  handleTriggerClick(event) {
    event.preventDefault();
    event.stopPropagation();

    // Close all other dropdowns before toggling this one
    if (!this.isOpen) {
      DropdownRegistry.closeAll(this);
    }

    this.toggle();
  }

  handleOptionClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const option = event.currentTarget;
    const value = option.dataset.value;
    const label = option.querySelector('[js-dropdown="option-label"]')?.textContent?.trim();
    const iconElement = option.querySelector('[js-dropdown="option-icon"]');

    this.selectOption(value, label, iconElement);
    this.close();
  }


  handleKeyDown(event) {
    if (!this.isOpen) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.open();
      }

      return;
    }

    const options = Array.from(this.querySelectorAll('[js-dropdown="option"]'));
    const currentIndex = options.findIndex(option => option.classList.contains('dropdown__option--focused'));

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        this.close();
        this.trigger?.focus();
        break;

      case 'Tab':
        // Handle Tab navigation within dropdown
        const activeElement = document.activeElement;
        if (activeElement === this.trigger) {
          // Tab from trigger should go to first option
          event.preventDefault();
          const firstOption = options[0];
          if (firstOption) {
            firstOption.focus();
            this.focusOption(options, 0);
          }
        }
        // Otherwise, let Tab work naturally to navigate through options
        // When Tab leaves the last option, dropdown will close via focusout handler
        break;

      case 'ArrowDown':
        event.preventDefault();
        this.focusNextOption(options, currentIndex);
        // Also focus the option for better screen reader support
        const nextIndex = currentIndex < options.length - 1 ? currentIndex + 1 : 0;
        if (options[nextIndex]) {
          options[nextIndex].focus();
        }
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.focusPreviousOption(options, currentIndex);
        // Also focus the option for better screen reader support
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : options.length - 1;
        if (options[prevIndex]) {
          options[prevIndex].focus();
        }
        break;

      case 'Enter':
      case ' ':
        event.preventDefault();
        const focusedOption = options[currentIndex];

        if (focusedOption) {
          this.handleOptionClick({ currentTarget: focusedOption, preventDefault: () => {}, stopPropagation: () => {} });
        }

        break;
    }
  }

  focusNextOption(options, currentIndex) {
    const nextIndex = currentIndex < options.length - 1 ? currentIndex + 1 : 0;

    this.focusOption(options, nextIndex);
  }

  focusPreviousOption(options, currentIndex) {
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : options.length - 1;

    this.focusOption(options, prevIndex);
  }

  focusOption(options, index) {
    options.forEach(option => option.classList.remove('dropdown__option--focused'));

    if (options[index]) {
      options[index].classList.add('dropdown__option--focused');
    }
  }

  selectOption(value, label, iconElement) {
    // Update selected state
    this.selectedValue = value;

    // Update hidden input
    if (this.hiddenInput) {
      this.hiddenInput.value = value;
    }

    // Update trigger display
    if (this.selectedLabel) {
      this.selectedLabel.textContent = label;
      this.selectedLabel.classList.remove('dropdown__placeholder');
    }

    // Update trigger icon
    if (this.selectedIcon && iconElement) {
      this.selectedIcon.innerHTML = iconElement.innerHTML;
    }

    // Update option selected states
    this.updateOptionStates(value);

    // Update ARIA attributes
    this.updateAriaAttributes(label);

    // Trigger custom event
    this.dispatchSelectionEvent(value, label);
  }

  updateOptionStates(selectedValue) {
    const options = this.querySelectorAll('[js-dropdown="option"]');

    options.forEach(option => {
      const isSelected = option.dataset.value === selectedValue;

      // Update classes
      option.classList.toggle('dropdown__option--selected', isSelected);

      // Update ARIA
      option.setAttribute('aria-selected', isSelected.toString());

      // Update selected indicator
      const indicator = option.querySelector('.dropdown__option-selected-indicator');

      if (isSelected && !indicator) {
        const newIndicator = document.createElement('div');

        newIndicator.className = 'dropdown__option-selected-indicator';

        // Use the tick icon SVG content
        newIndicator.innerHTML = `
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M23 8.26476L10.7678 20.4969L5 14.7291L5.76183 13.9672L10.7678 18.9733L22.2382 7.50293L23 8.26476Z" fill="currentColor" stroke="currentColor" stroke-width="0.5"/>
          </svg>
        `;

        option.appendChild(newIndicator);
      } else if (!isSelected && indicator) {
        indicator.remove();
      }
    });
  }

  updateAriaAttributes(label) {
    if (this.trigger) {
      this.trigger.setAttribute('aria-label', label);
    }
  }

  dispatchSelectionEvent(value, label) {
    const event = new CustomEvent('dropdown:change', {
      detail: {
        name: this.name,
        value: value,
        label: label,
        dropdown: this
      },
      bubbles: true
    });

    this.dispatchEvent(event);
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    if (this.isOpen) return;

    this.isOpen = true;
    this.optionsContainer?.classList.add('is-active');
    this.classList.add('dropdown--open');
    this.trigger?.setAttribute('aria-expanded', 'true');

    // Make options focusable when open
    this.updateOptionsTabindex(true);

    // Adjust positioning to prevent overflow
    this.adjustDropdownPosition();

    // Focus first option for keyboard navigation
    const firstOption = this.querySelector('[js-dropdown="option"]');

    if (firstOption) {
      this.focusOption([firstOption], 0);
    }

    // Dispatch open event
    this.dispatchEvent(new CustomEvent('dropdown:open', {
      detail: { name: this.name, dropdown: this },
      bubbles: true
    }));
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.optionsContainer?.classList.remove('is-active');
    this.classList.remove('dropdown--open');
    this.trigger?.setAttribute('aria-expanded', 'false');

    // Make options non-focusable when closed
    this.updateOptionsTabindex(false);

    // Reset positioning
    this.resetDropdownPosition();

    // Remove focus states
    const options = this.querySelectorAll('[js-dropdown="option"]');
    options.forEach(option => option.classList.remove('dropdown__option--focused'));

    // Dispatch close event
    this.dispatchEvent(new CustomEvent('dropdown:close', {
      detail: { name: this.name, dropdown: this },
      bubbles: true
    }));
  }

  adjustDropdownPosition() {
    if (!this.optionsContainer) return;

    // Reset any previous adjustments
    this.optionsContainer.style.removeProperty('left');
    this.optionsContainer.style.removeProperty('right');
    this.optionsContainer.style.removeProperty('translate');

    // Wait for next frame to get accurate measurements
    requestAnimationFrame(() => {
      const rect = this.optionsContainer.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const padding = 20; // Minimum padding from viewport edges

      // Check if dropdown overflows on the right
      if (rect.right > viewportWidth - padding) {
        this.optionsContainer.style.left = 'auto';
        this.optionsContainer.style.right = '0';
        this.optionsContainer.style.translate = 'none';
      }
      // Check if dropdown overflows on the left
      else if (rect.left < padding) {
        this.optionsContainer.style.left = '0';
        this.optionsContainer.style.translate = 'none';
      }
      // Otherwise keep default centered position
    });
  }

  resetDropdownPosition() {
    if (!this.optionsContainer) return;

    this.optionsContainer.style.removeProperty('left');
    this.optionsContainer.style.removeProperty('right');
    this.optionsContainer.style.removeProperty('translate');
  }

  // Public API methods
  getValue() {
    return this.selectedValue;
  }

  setValue(value) {
    const option = this.querySelector(`[js-dropdown="option"][data-value="${value}"]`);

    if (option) {
      const label = option.querySelector('[js-dropdown="option-label"]')?.textContent?.trim();
      const iconElement = option.querySelector('[js-dropdown="option-icon"]');

      this.selectOption(value, label, iconElement);
    }
  }

  getLabel() {
    return this.selectedLabel?.textContent?.trim() || '';
  }
}

window.customElements.define("el-dropdown", Dropdown);
